import type { WhiteLabelBranding } from './whiteLabel';
import { currentRuntime } from '../config/runtime';

export type WhiteLabelAppBranding = {
  tenantSlug?: string;
  appName: string;
  companyName: string;
  shortProductName: string;
  supportEmail: string;
  legalEntityName: string;
  logoHref: string | null;
  /** Light-theme variant of the primary logo. Null when the tenant has none. */
  logoLightHref: string | null;
  sidebarLogoHref: string | null;
  /** Light-theme variant of the sidebar logo. Null when the tenant has none. */
  sidebarLogoLightHref: string | null;
  compactLogoHref: string | null;
  /** Light-theme variant of the compact / logo mark. Null when the tenant has none. */
  compactLogoLightHref: string | null;
  loadingPageLogoHref: string | null;
  loadingPageLogoSize?: 'small' | 'medium' | 'large' | 'x-large';
  loadingPageLogoGlow?: boolean;
  useTextLogo?: boolean;
  faviconHref: string | null;
  sidebarLogoBottomSpacing?: number;
  primaryColor: string | null;
  secondaryColor: string | null;
  partnerRevenueShare: number;
  partnerMonthlyPrice: number;
  wording: Record<string, string>;
};

const DEFAULT_DOCUMENT_TITLE = 'Trade-Proof';
const NEUTRAL_DOCUMENT_TITLE = 'Trading workspace';

// Empty/transparent SVG data URI used as the neutral favicon while tenant branding
// is loading — or when a tenant has no custom favicon. Declaring a favicon also
// stops the browser from auto-requesting /favicon.ico, which would otherwise serve
// the deployment's base Trade-Proof favicon on tenant hostnames.
const NEUTRAL_FAVICON_HREF =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'/%3E";

function readLink(rel: string): HTMLLinkElement | null {
  return document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
}

function upsertFavicon(href: string): HTMLLinkElement {
  const existing = readLink('icon') || readLink('shortcut icon');
  if (existing) {
    existing.href = href;
    return existing;
  }
  const link = document.createElement('link');
  link.rel = 'icon';
  link.href = href;
  document.head.appendChild(link);
  return link;
}

/**
 * Appends a version query to a brand-asset URL (logos and favicon) so browsers
 * never serve a cached copy after the tenant's branding is updated. The storage
 * upload path already uses a fresh object key per upload; the config `updatedAt`
 * version also covers external URLs and CDN edge caches. URLs without a version
 * value are returned unchanged.
 */
export function withAssetVersion(href: string | null, updatedAt?: string | null): string | null {
  const url = String(href || '').trim();
  const version = String(updatedAt || '').trim();
  if (!url || !version) return url || null;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${encodeURIComponent(version)}`;
}

export function buildWhiteLabelAppBranding(branding: WhiteLabelBranding): WhiteLabelAppBranding {
  return {
    tenantSlug: branding.tenantSlug,
    appName: branding.companyName || branding.productName || 'Trade-Proof',
    companyName: branding.companyName || branding.productName || 'Trade-Proof',
    shortProductName: branding.shortProductName || branding.productName || 'Trade-Proof',
    supportEmail: branding.supportEmail || '',
    legalEntityName: branding.legalEntityName || branding.companyName || 'Trade-Proof',
    logoHref: withAssetVersion(branding.primaryLogoUrl, branding.publicBrandingUpdatedAt),
    logoLightHref: withAssetVersion(branding.primaryLogoLightUrl, branding.publicBrandingUpdatedAt),
    sidebarLogoHref: withAssetVersion(branding.sidebarLogoUrl, branding.publicBrandingUpdatedAt),
    sidebarLogoLightHref: withAssetVersion(branding.sidebarLogoLightUrl, branding.publicBrandingUpdatedAt),
    compactLogoHref: withAssetVersion(branding.compactLogoUrl, branding.publicBrandingUpdatedAt),
    compactLogoLightHref: withAssetVersion(branding.compactLogoLightUrl, branding.publicBrandingUpdatedAt),
    loadingPageLogoHref: withAssetVersion(branding.loadingPageLogoUrl, branding.publicBrandingUpdatedAt),
    loadingPageLogoSize: branding.loadingPageLogoSize || 'medium',
    loadingPageLogoGlow: branding.loadingPageLogoGlow !== false,
    useTextLogo: branding.useTextLogo === true,
    faviconHref: withAssetVersion(branding.faviconUrl, branding.publicBrandingUpdatedAt),
    sidebarLogoBottomSpacing: Math.max(0, Number(branding.sidebarLogoBottomSpacing) || 0),
    primaryColor: branding.primaryColor || null,
    secondaryColor: branding.secondaryColor || null,
    partnerRevenueShare: typeof branding.partnerRevenueShare === 'number' ? branding.partnerRevenueShare : 35,
    partnerMonthlyPrice: typeof branding.partnerMonthlyPrice === 'number' && branding.partnerMonthlyPrice >= 0 ? branding.partnerMonthlyPrice : 39,
    wording: { ...branding.wording },
  };
}

/**
 * Theme-aware expanded-sidebar logo. Uses the theme's variant when present,
 * falling back to the other variant, then Primary / Compact / default (null).
 *   Sidebar Logo (theme) -> Sidebar Logo (other) -> Primary -> Compact -> null.
 */
export function resolveSidebarExpandedLogo(
  branding: WhiteLabelAppBranding | null,
  theme: 'light' | 'dark',
): string | null {
  if (!branding) return null;
  const themeHref = theme === 'light' ? branding.sidebarLogoLightHref : branding.sidebarLogoHref;
  const otherHref = theme === 'light' ? branding.sidebarLogoHref : branding.sidebarLogoLightHref;
  const primaryHref = theme === 'light' ? branding.logoLightHref || branding.logoHref : branding.logoHref || branding.logoLightHref;
  const compactHref = theme === 'light' ? branding.compactLogoLightHref || branding.compactLogoHref : branding.compactLogoHref || branding.compactLogoLightHref;
  return themeHref || otherHref || primaryHref || compactHref || null;
}

/**
 * Theme-aware collapsed-sidebar logo: always the compact / logo mark, using the
 * theme's variant when present and falling back to the other variant. Falls back
 * to the Trade-Proof default symbol (null) when the tenant has no compact logo.
 */
export function resolveSidebarCollapsedLogo(
  branding: WhiteLabelAppBranding | null,
  theme: 'light' | 'dark',
): string | null {
  if (!branding) return null;
  const themeHref = theme === 'light' ? branding.compactLogoLightHref : branding.compactLogoHref;
  const otherHref = theme === 'light' ? branding.compactLogoHref : branding.compactLogoLightHref;
  return themeHref || otherHref || null;
}

/**
 * Theme-aware compact / logo mark for non-sidebar surfaces (e.g. the workspace
 * loading screen). Uses the theme's variant when present, otherwise the other.
 */
export function resolveThemeAwareCompactLogo(
  branding: WhiteLabelAppBranding | null,
  theme: 'light' | 'dark',
): string | null {
  return resolveSidebarCollapsedLogo(branding, theme);
}

/**
 * Theme-aware primary logo. Use the existing Primary Logo on dark backgrounds and
 * the Primary Logo (Light) on light backgrounds when available. Falls back to the
 * Primary Logo when no light variant has been uploaded, and to null when the tenant
 * has no primary logo at all.
 */
export function resolveThemeAwarePrimaryLogo(
  branding: WhiteLabelAppBranding | null,
  theme: 'light' | 'dark',
): string | null {
  if (!branding) return null;
  if (theme === 'light' && branding.logoLightHref) return branding.logoLightHref;
  return branding.logoHref;
}

function hexToHslToken(value: string): string | null {
  const hex = String(value || '').replace(/^#/, '');
  const normalized = hex.length === 3 ? hex.split('').map((part) => `${part}${part}`).join('') : hex;
  if (!/^[0-9a-f]{6}$/i.test(normalized)) return null;
  const channels = [0, 2, 4].map((index) => parseInt(normalized.slice(index, index + 2), 16) / 255);
  const max = Math.max(...channels);
  const min = Math.min(...channels);
  const lightness = (max + min) / 2;
  if (max === min) return `0 0% ${Math.round(lightness * 1000) / 10}%`;
  const delta = max - min;
  const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  let hue = 0;
  if (max === channels[0]) hue = ((channels[1] - channels[2]) / delta + (channels[1] < channels[2] ? 6 : 0));
  else if (max === channels[1]) hue = (channels[2] - channels[0]) / delta + 2;
  else hue = (channels[0] - channels[1]) / delta + 4;
  return `${Math.round((hue / 6) * 3600) / 10} ${Math.round(saturation * 1000) / 10}% ${Math.round(lightness * 1000) / 10}%`;
}

export function applyWhiteLabelBrandingToDocument(branding: WhiteLabelAppBranding | null) {
  if (typeof document === 'undefined') return;

  if (branding?.appName) {
    document.title = branding.appName;
  } else {
    document.title = DEFAULT_DOCUMENT_TITLE;
  }

  const root = document.documentElement;
  if (branding?.primaryColor) {
    root.style.setProperty('--white-label-accent', branding.primaryColor);
    const primaryToken = hexToHslToken(branding.primaryColor);
    if (primaryToken) root.style.setProperty('--primary', primaryToken);
  } else {
    root.style.removeProperty('--white-label-accent');
    root.style.removeProperty('--primary');
  }
  const secondaryToken = branding?.secondaryColor ? hexToHslToken(branding.secondaryColor) : null;
  if (secondaryToken) root.style.setProperty('--secondary', secondaryToken);
  else root.style.removeProperty('--secondary');

  const favicon = readLink('icon') || readLink('shortcut icon');
  if (branding?.faviconHref) {
    if (favicon) {
      favicon.href = branding.faviconHref;
    } else {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = branding.faviconHref;
      document.head.appendChild(link);
    }
  } else if (branding) {
    // Tenant branding without a custom favicon: replace any previous favicon with
    // the neutral placeholder so a stale tenant icon never persists in the tab and
    // the browser never falls back to the deployment's base favicon.
    upsertFavicon(NEUTRAL_FAVICON_HREF);
  } else if (favicon && favicon.href.includes('white-label')) {
    favicon.remove();
  }
}

export function clearWhiteLabelBrandingFromDocument() {
  applyWhiteLabelBrandingToDocument(null);
}

/**
 * Neutral pre-branding document state for tenant hostnames while tenant branding
 * is still resolving (or is withheld for an unpublished draft). Must never reveal
 * the default Trade-Proof product name, accent color, or favicon. A neutral
 * favicon is declared (instead of removing icon links) so the browser does not
 * auto-request /favicon.ico — which would serve the base Trade-Proof favicon.
 */
export function applyNeutralWhiteLabelBrandingToDocument() {
  if (typeof document === 'undefined') return;

  document.title = NEUTRAL_DOCUMENT_TITLE;
  document.documentElement.style.removeProperty('--white-label-accent');
  document.documentElement.style.removeProperty('--primary');
  document.documentElement.style.removeProperty('--secondary');
  upsertFavicon(NEUTRAL_FAVICON_HREF);
  document
    .querySelectorAll(
      'link[rel="apple-touch-icon"], link[rel="manifest"]',
    )
    .forEach((link) => link.remove());
}

// ---------------------------------------------------------------------------
// Synchronous branding cache — makes the tenant logo available on the loading
// screen's FIRST paint instead of after the async branding fetch + re-render.
// sessionStorage is scoped per hostname (a different tenant host never reads
// another tenant's cached branding) and survives page reloads within a tab,
// while never leaking across sessions. The module-level value lets repeat
// renders within the same page load read it without touching storage.
// ---------------------------------------------------------------------------

const BRANDING_CACHE_KEY_PREFIX = 'tt:white-label-app-branding:';

// undefined = not read yet. Hostname-scoped so a tenant host never serves another
// tenant's cached branding even within the same page/session (e.g. wildcard
// subdomains or test environments that mutate window.location).
let cachedAppBrandingEntry: { hostname: string; branding: WhiteLabelAppBranding | null } | undefined;

function readBrandingCacheHostname(): string {
  try {
    return String(window.location.hostname || '').trim().toLowerCase();
  } catch {
    return '';
  }
}

function readBrandingFromSessionStorage(hostname: string): WhiteLabelAppBranding | null {
  if (!hostname) return null;
  try {
    const raw = window.sessionStorage.getItem(`${BRANDING_CACHE_KEY_PREFIX}${hostname}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WhiteLabelAppBranding | null;
    return parsed && typeof parsed === 'object' && parsed.companyName ? parsed : null;
  } catch {
    return null;
  }
}

function preloadBrandLogoImages(branding: WhiteLabelAppBranding | null) {
  if (!branding || typeof Image === 'undefined') return;
  // Warm the browser cache for the logo marks used by the loading screen and
  // chrome so the first <img> paint is instant instead of a second network hop.
  for (const href of [branding.compactLogoHref, branding.logoHref, branding.sidebarLogoHref]) {
    if (!href) continue;
    try {
      const img = new Image();
      img.src = href;
    } catch {
      // Best-effort preload; never blocks boot or branding application.
    }
  }
}

/**
 * Synchronously cached tenant app branding (scoped to the current hostname). Used
 * to seed the app's branding state so the workspace loading screen can render the
 * correct tenant logo on its very first paint (e.g. on reload) instead of flashing
 * an empty shield while the async branding fetch resolves.
 */
export function getCachedWhiteLabelAppBranding(): WhiteLabelAppBranding | null {
  const hostname = readBrandingCacheHostname();
  if (cachedAppBrandingEntry !== undefined && cachedAppBrandingEntry.hostname === hostname) {
    return cachedAppBrandingEntry.branding;
  }
  const branding = readBrandingFromSessionStorage(hostname);
  cachedAppBrandingEntry = { hostname, branding };
  return branding;
}

/**
 * Cache the resolved tenant branding synchronously and preload its logo images.
 * Every successful branding resolution (public pre-login and authenticated) writes
 * here so the next render/reload has the logo immediately.
 */
export function setCachedWhiteLabelAppBranding(branding: WhiteLabelAppBranding | null) {
  const hostname = readBrandingCacheHostname();
  cachedAppBrandingEntry = { hostname, branding };
  try {
    if (hostname) {
      if (branding) {
        window.sessionStorage.setItem(`${BRANDING_CACHE_KEY_PREFIX}${hostname}`, JSON.stringify(branding));
      } else {
        window.sessionStorage.removeItem(`${BRANDING_CACHE_KEY_PREFIX}${hostname}`);
      }
    }
  } catch {
    // Storage can be unavailable (private mode / disabled) — the module-level
    // cache still covers the current page load.
  }
  preloadBrandLogoImages(branding);
}

/**
 * Shared tenant-facing product/platform name for user-facing copy.
 *
 * White-label tenants use their configured Company Name / branded product name
 * (companyName or appName), so every existing and future white-label tenant is
 * branded automatically. Base tenants fall back to the runtime brand display
 * name (Trade-Proof / Warrior Trading), then the default document title.
 */
export function resolveTenantProductName(): string {
  const cached = getCachedWhiteLabelAppBranding();
  const whiteLabelName = String(cached?.companyName || cached?.appName || '').trim();
  const runtimeName = String(currentRuntime.brand.displayName || '').trim();
  return whiteLabelName || runtimeName || DEFAULT_DOCUMENT_TITLE;
}
